import { VariantProps } from "class-variance-authority";
import {
  ComponentType,
  DetailedHTMLProps,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  Ref,
} from "react";
import {
  alertHeaderVariants,
  alertIconVariants,
  alertVariants,
} from "../helpers/alert";
import { AlertTriangleIcon, AnnotationInfoIcon } from "@/icons";
import { Slot } from "@radix-ui/react-slot";

type Required0<T extends object> = {
  [K0 in keyof T]-?: NonNullable<T[K0]>;
};
type MakeRequired<T extends object, Fields extends keyof T> = Omit<T, Fields> &
  Required0<Pick<T, Fields>>;

export interface AlertProps
  extends
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    MakeRequired<VariantProps<typeof alertVariants>, "kind"> {
  header: ReactNode;
  children: ReactNode;
  asChild?: boolean | undefined;
}

export type AlertKind = AlertProps["kind"];

export const Alert = forwardRef(function Alert(
  { kind, header, children, className, asChild, ...props }: AlertProps,
  ref: Ref<HTMLDivElement>,
) {
  const Icon = icons[kind];
  const Body = asChild ? Slot : "p";
  return (
    <div ref={ref} {...props} className={alertVariants({ kind, className })}>
      <span className={alertIconVariants({ kind })}>
        <Icon />
      </span>
      <div className="flex flex-col gap-1">
        <h4 className={alertHeaderVariants({ kind })}>{header}</h4>
        <Body className="text-grey-900 text-sm">{children}</Body>
      </div>
    </div>
  );
});

const icons: Record<AlertKind, ComponentType> = {
  warning: AlertTriangleIcon,
  info: AnnotationInfoIcon,
};
